import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
//import { toast } from 'react-toastify';
import toast from '../../utils/toast';
import { generateCustomIdsForList } from '../../utils/customIdGenerator';
import { FaPlus, FaDownload, FaEdit, FaTrash, FaEye, FaExchangeAlt, FaEnvelope, FaCreditCard } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGuard from '../PermissionGuard';

const InvoiceList = () => {
  const navigate = useNavigate();
  const { checkPermission } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const [currentPayment, setCurrentPayment] = useState(null);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    paymentStatus: '',
    search: '',
    page: 1
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Function to calculate pending days left
  const calculatePendingTime = (dueDate) => {
    if (!dueDate) return 'No due date';
    
    const now = new Date();
    const due = new Date(dueDate);
    const diffInMs = due - now;
    
    if (diffInMs <= 0) {
      return 'Overdue';
    }
    
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    const remainingHours = diffInHours % 24;
    const remainingMinutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffInDays > 0) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ${remainingHours > 0 ? `${remainingHours}h` : ''}`;
    } else if (diffInHours > 0) {
      return `${diffInHours}h ${remainingMinutes > 0 ? `${remainingMinutes}m` : ''}`;
    } else {
      return `${remainingMinutes}m`;
    }
  };

  // Function to get color based on pending time
  const getPendingTimeColor = (dueDate) => {
    if (!dueDate) return 'text-gray-500';
    
    const now = new Date();
    const due = new Date(dueDate);
    const diffInMs = due - now;
    const diffInHours = diffInMs / (1000 * 60 * 60);
    
    if (diffInMs <= 0) return 'text-red-600 font-semibold';
    if (diffInHours < 24) return 'text-orange-600 font-semibold';
    if (diffInHours < 72) return 'text-yellow-600';
    return 'text-green-600';
  };

  useEffect(() => {
    fetchInvoices();
    loadPaymentSettings();
  }, [filters]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await api.get(`/invoices?${params.toString()}`);
      const invoicesWithCustomIds = await generateCustomIdsForList('Invoice', response.data.data);
      setInvoices(invoicesWithCustomIds);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to fetch invoices');
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentSettings = async () => {
    try {
      const response = await api.get('/payment-settings');
      setPaymentSettings(response.data.data);
    } catch (error) {
      console.error('Error loading payment settings:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) {
      return;
    }

    try {
      await api.delete(`/invoices/${id}`);
      toast.success('Invoice deleted successfully');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleDownload = async (id) => {
    try {
      const response = await api.get(`/invoices/${id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleConvertProforma = async (id) => {
    try {
      const response = await api.post(`/invoices/${id}/convert`);
      toast.success('Proforma invoice converted successfully');
      navigate(`/invoices/${response.data._id}`);
    } catch (error) {
      toast.error('Failed to convert proforma invoice');
    }
  };

  const handleEmail = async (id) => {
    try {
      await api.post(`/invoices/${id}/email`);
      toast.success('Invoice email sent successfully');
    } catch (error) {
      toast.error('Failed to send invoice email');
    }
  };

  // Payment processing functions
  const handlePayment = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
    setPaymentStatus('idle');
    setCurrentPayment(null);
  };

  const createPayment = async (gateway) => {
    try {
      setPaymentLoading(true);
      setPaymentStatus('processing');

      const paymentData = {
        invoiceId: selectedInvoice._id,
        gateway,
        amount: selectedInvoice.totalAmount,
        description: `Payment for ${selectedInvoice.type === 'proforma' ? 'Proforma Invoice' : 'Invoice'}: ${selectedInvoice.invoiceNumber}`
      };

      const response = await api.post('/payments/invoice', paymentData);
      const payment = response.data.data;
      
      setCurrentPayment(payment);
      
      // Handle different payment gateways
      switch (gateway) {
        case 'stripe':
          handleStripePayment(payment);
          break;
        case 'paypal':
          handlePayPalPayment(payment);
          break;
        case 'razorpay':
          handleRazorpayPayment(payment);
          break;
        case 'bank_transfer':
          handleBankTransferPayment(payment);
          break;
        default:
          toast.error('Invalid payment gateway');
          setPaymentStatus('failed');
      }
    } catch (error) {
      console.error('Failed to create payment:', error);
      toast.error(error.response?.data?.message || 'Failed to create payment');
      setPaymentStatus('failed');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleStripePayment = async (payment) => {
    try {
      // Get payment data from backend
      const response = await api.get(`/payments/${payment.paymentId || payment._id}`);
      const paymentData = response.data.data;
      
      if (paymentData.metadata && paymentData.metadata.clientSecret) {
        // Redirect to Stripe payment page or use Stripe Elements
        toast.info('Redirecting to Stripe payment page...');
        
        // Simulate payment completion for demo
        setTimeout(async () => {
          await checkPaymentStatus(payment.paymentId || payment._id);
        }, 3000);
      } else {
        toast.error('Payment data not available');
        setPaymentStatus('failed');
      }
    } catch (error) {
      console.error('Stripe payment error:', error);
      setPaymentStatus('failed');
      toast.error('Payment failed. Please try again.');
    }
  };

  const handlePayPalPayment = async (payment) => {
    try {
      // Get payment data from backend
      const response = await api.get(`/payments/${payment.paymentId || payment._id}`);
      const paymentData = response.data.data;
      
      if (paymentData.metadata && paymentData.metadata.approvalUrl) {
        // Redirect to PayPal payment page
        window.open(paymentData.metadata.approvalUrl, '_blank');
        
        // Check payment status periodically
        const checkInterval = setInterval(async () => {
          const statusResponse = await api.get(`/payments/${payment.paymentId || payment._id}`);
          const statusData = statusResponse.data.data;
          
          if (statusData.status === 'completed') {
            clearInterval(checkInterval);
            setPaymentStatus('completed');
            toast.success('Payment completed successfully!');
            setShowPaymentModal(false);
            fetchInvoices(); // Refresh the list
          } else if (statusData.status === 'failed') {
            clearInterval(checkInterval);
            setPaymentStatus('failed');
            toast.error('Payment failed. Please try again.');
          }
        }, 5000); // Check every 5 seconds
        
        // Stop checking after 10 minutes
        setTimeout(() => {
          clearInterval(checkInterval);
        }, 600000);
      } else {
        toast.error('PayPal payment data not available');
        setPaymentStatus('failed');
      }
    } catch (error) {
      console.error('PayPal payment error:', error);
      setPaymentStatus('failed');
      toast.error('Payment failed. Please try again.');
    }
  };

  const handleRazorpayPayment = async (payment) => {
    try {
      // Get payment data from backend
      const response = await api.get(`/payments/${payment.paymentId || payment._id}`);
      const paymentData = response.data.data;
      
      if (paymentData.metadata && paymentData.metadata.orderId) {
        // Load Razorpay script if not already loaded
        if (!window.Razorpay) {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = () => initializeRazorpayPayment(paymentData);
          document.head.appendChild(script);
        } else {
          initializeRazorpayPayment(paymentData);
        }
      } else {
        toast.error('Razorpay payment data not available');
        setPaymentStatus('failed');
      }
    } catch (error) {
      console.error('Razorpay payment error:', error);
      setPaymentStatus('failed');
      toast.error('Payment failed. Please try again.');
    }
  };

  const initializeRazorpayPayment = (paymentData) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_YOUR_KEY_ID',
      amount: paymentData.amount * 100, // Razorpay expects amount in paise
      currency: paymentData.currency,
      order_id: paymentData.metadata.orderId,
      name: 'Invoice Payment',
      description: paymentData.description,
      handler: async function (response) {
        // Payment successful
        try {
          await checkPaymentStatus(paymentData.paymentId);
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
      },
      prefill: {
        name: selectedInvoice?.billingAddress?.name || '',
        email: selectedInvoice?.billingAddress?.email || ''
      },
      theme: {
        color: '#3B82F6'
      }
    };
    
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const handleBankTransferPayment = (payment) => {
    // Bank transfer shows instructions
    setPaymentStatus('pending');
    toast.info('Bank transfer details provided. Please complete the transfer.');
  };

  const checkPaymentStatus = async (paymentId) => {
    try {
      const response = await api.get(`/payments/${paymentId}`);
      const payment = response.data.data;
      
      setCurrentPayment(payment);
      setPaymentStatus(payment.status);
      
      if (payment.status === 'completed') {
        toast.success('Payment completed successfully!');
        setShowPaymentModal(false);
        fetchInvoices(); // Refresh the list
      } else if (payment.status === 'failed') {
        toast.error('Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('failed');
    }
  };

  const cancelPayment = async (paymentId) => {
    try {
      await api.post(`/payments/${paymentId}/cancel`);
      setPaymentStatus('cancelled');
      toast.info('Payment cancelled');
    } catch (error) {
      console.error('Error cancelling payment:', error);
      toast.error('Failed to cancel payment');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      unpaid: 'bg-red-100 text-red-800',
      cancelled: 'bg-red-100 text-red-800',
      overdue: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      partial: 'bg-orange-100 text-orange-800',
      paid: 'bg-green-100 text-green-800',
      unpaid: 'bg-red-100 text-red-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getInvoiceTypeColor = (type) => {
    const colors = {
      invoice: 'bg-blue-100 text-blue-800',
      proforma: 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getInvoiceTypeLabel = (invoice) => {
    if (invoice.type === 'proforma') {
      if (invoice.convertedTo) {
        return 'Proforma (Converted)';
      }
      return 'Proforma';
    }
    if (invoice.convertedFrom) {
      return 'Invoice (From Proforma)';
    }
    return 'Invoice';
  };

  // Get simplified status for display
  const getSimplifiedStatus = (invoice) => {
    if (invoice.status === 'paid' || invoice.paymentStatus === 'paid') {
      return 'Paid';
    }
    return 'Unpaid';
  };

  // Get enabled payment gateways
  const getEnabledGateways = () => {
    if (!paymentSettings) return [];
    
    const enabledGateways = [];
    if (paymentSettings.stripe?.enabled) enabledGateways.push('stripe');
    if (paymentSettings.paypal?.enabled) enabledGateways.push('paypal');
    if (paymentSettings.razorpay?.enabled) enabledGateways.push('razorpay');
    if (paymentSettings.bankTransfer?.enabled) enabledGateways.push('bank_transfer');
    
    return enabledGateways;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Invoices</h1>
        <PermissionGuard requiredPermission="Create Invoice">
          <button
            onClick={() => navigate('/invoices/new')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FaPlus className="mr-2" />
            Create Invoice
          </button>
        </PermissionGuard>
      </div>

      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              <option value="invoice">Invoice</option>
              <option value="proforma">Proforma</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="cancelled">Cancelled</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Invoice number, customer, user..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ type: '', status: '', paymentStatus: '', search: '', page: 1 })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Custom ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer/User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pending Days Left
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {invoice.customId || invoice._id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {invoice.invoiceNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getInvoiceTypeColor(invoice.type)}`}>
                      {getInvoiceTypeLabel(invoice)}
                    </span>
                    {invoice.convertedFrom && (
                      <div className="text-xs text-gray-400 mt-1">
                        From: {invoice.convertedFrom.invoiceNumber}
                      </div>
                    )}
                    {invoice.convertedTo && (
                      <div className="text-xs text-gray-400 mt-1">
                        Converted to: {invoice.convertedTo.invoiceNumber}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {invoice.formSubmission?.userId && typeof invoice.formSubmission.userId === 'object'
                      ? `${invoice.formSubmission.userId.firstName} ${invoice.formSubmission.userId.lastName} (${invoice.billingAddress?.name})`
                      : invoice.billingAddress?.name || 'N/A'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${invoice.totalAmount?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(getSimplifiedStatus(invoice))}`}>
                      {getSimplifiedStatus(invoice)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={getPendingTimeColor(invoice.dueDate)}>
                      {calculatePendingTime(invoice.dueDate)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <PermissionGuard requiredPermission="View Invoice">
                        <button
                          onClick={() => navigate(`/invoices/${invoice._id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View"
                        >
                          <FaEye />
                        </button>
                      </PermissionGuard>
                      
                      <PermissionGuard requiredPermission="View Invoice">
                        <button
                          onClick={() => handleDownload(invoice._id)}
                          className="text-green-600 hover:text-green-900"
                          title="Download"
                        >
                          <FaDownload />
                        </button>
                      </PermissionGuard>
                      
                      <PermissionGuard requiredPermission="View Invoice">
                        <button
                          onClick={() => handleEmail(invoice._id)}
                          className="text-orange-600 hover:text-orange-900"
                          title="Send Email"
                        >
                          <FaEnvelope />
                        </button>
                      </PermissionGuard>
                      
                      {/* Payment Action for Proforma Invoices */}
                      {invoice.type === 'proforma' && getSimplifiedStatus(invoice) === 'Unpaid' && checkPermission('Create Invoice') && (
                        <button
                          onClick={() => handlePayment(invoice)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Process Payment"
                        >
                          <FaCreditCard />
                        </button>
                      )}
                      
                      {invoice.type === 'proforma' && checkPermission('Create Invoice') && (
                        <button
                          onClick={() => handleConvertProforma(invoice._id)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Convert to Invoice"
                        >
                          <FaExchangeAlt />
                        </button>
                      )}
                      
                      <PermissionGuard requiredPermission="Update Invoice">
                        <button
                          onClick={() => navigate(`/invoices/${invoice._id}/edit`)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                      </PermissionGuard>
                      
                      <PermissionGuard requiredPermission="Delete Invoice">
                        <button
                          onClick={() => handleDelete(invoice._id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </PermissionGuard>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Process Payment</h3>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              {selectedInvoice && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600">Invoice: {selectedInvoice.invoiceNumber}</p>
                  <p className="text-sm text-gray-600">Amount: ${selectedInvoice.totalAmount?.toFixed(2)}</p>
                </div>
              )}

              {paymentStatus === 'idle' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">Select a payment method:</p>
                  <div className="space-y-2">
                    {getEnabledGateways().map((gateway) => (
                      <button
                        key={gateway}
                        onClick={() => createPayment(gateway)}
                        disabled={paymentLoading}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {paymentLoading ? 'Processing...' : `Pay with ${gateway.charAt(0).toUpperCase() + gateway.slice(1)}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {paymentStatus === 'processing' && (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Processing payment...</p>
                </div>
              )}

              {paymentStatus === 'completed' && (
                <div className="text-center">
                  <div className="text-green-600 text-2xl mb-2">✓</div>
                  <p className="text-sm text-gray-600">Payment completed successfully!</p>
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Close
                  </button>
                </div>
              )}

              {paymentStatus === 'failed' && (
                <div className="text-center">
                  <div className="text-red-600 text-2xl mb-2">✗</div>
                  <p className="text-sm text-gray-600">Payment failed. Please try again.</p>
                  <button
                    onClick={() => setPaymentStatus('idle')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {paymentStatus === 'pending' && (
                <div className="text-center">
                  <div className="text-yellow-600 text-2xl mb-2">⏳</div>
                  <p className="text-sm text-gray-600">Payment is pending. Please complete the transfer.</p>
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceList; 